import { useEffect, useRef, useState } from "react";
import { usePathname } from "../../lib/router";
import { Button } from "../../components/ui/button";
import { Loader2, CheckCircle, AlertCircle, PenTool, Eraser, ShieldCheck } from "lucide-react";
import { cn } from "../../lib/utils";

export default function PublicSigningPage() {
  const path = usePathname();
  const token = path.startsWith("/sign/") ? path.split("/")[2] : null;
  const [contract, setContract] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signed, setSigned] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    fetchContract();
  }, [token]);

  const fetchContract = async () => {
    try {
      const res = await fetch(`/api/public/contracts/${token}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao carregar");
      setContract(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e: any) => {
    isDrawing.current = true;
    draw(e);
  };

  const stopDrawing = () => {
    isDrawing.current = false;
    const ctx = canvasRef.current?.getContext("2d");
    ctx?.beginPath();
  };

  const draw = (e: any) => {
    if (!isDrawing.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#171717";

    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
    
    if (e.touches) e.preventDefault();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (ctx && canvas) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleSign = async () => {
    if (!canvasRef.current) return;
    const signature = canvasRef.current.toDataURL("image/png");
    
    setSigning(true);
    try {
      const res = await fetch(`/api/public/contracts/${token}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature })
      });
      if (res.ok) {
        setSigned(true);
      } else {
        const data = await res.json();
        alert(data.error || "Erro ao assinar");
      }
    } catch (e) {
      alert("Erro na conexão");
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center p-8">
      <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mb-4" />
      <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">Carregando documento...</p>
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl border border-neutral-100 text-center animate-in zoom-in-95 duration-200">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Ops! Algo deu errado</h2>
        <p className="text-neutral-500 font-medium mb-8 leading-relaxed">{error}</p>
        <Button 
          onClick={() => window.location.reload()}
          className="w-full h-12 bg-neutral-900 hover:bg-neutral-800 text-white font-bold rounded-2xl transition-all active:scale-95"
        >
          Tentar novamente
        </Button>
      </div>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl border border-neutral-100 text-center animate-in zoom-in-95 duration-200">
        <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Assinado com sucesso!</h2>
        <p className="text-neutral-500 font-medium mb-8 leading-relaxed">
          Obrigado por assinar o documento. Uma cópia assinada já foi registrada no sistema.
        </p>
        <div className="p-4 rounded-2xl bg-neutral-50 border border-neutral-100 flex items-center gap-3 text-left">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-neutral-800 tracking-tight">Ambiente Seguro</p>
            <p className="text-[10px] text-neutral-400 font-medium uppercase tracking-widest">Documento Registrado</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 pb-20">
      <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center text-white shadow-lg shadow-emerald-600/20">
              <PenTool className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 tracking-tight leading-none mb-2">Assinatura de Documento</h1>
              <p className="text-xs text-neutral-400 font-medium uppercase tracking-widest">{contract.title}</p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-[32px] border border-neutral-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-neutral-50 bg-neutral-50/30 flex items-center gap-2">
            <div className="w-1.5 h-4 bg-emerald-500 rounded-full" />
            <span className="text-[10px] font-bold uppercase text-neutral-500 tracking-[0.2em]">
              Conteúdo do Documento
            </span>
          </div>
          <div className="p-8 md:p-12 overflow-y-auto max-h-[60vh] custom-scrollbar">
            <div className="whitespace-pre-wrap text-neutral-800 leading-relaxed text-sm md:text-base font-medium">
              {contract.content}
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="bg-white rounded-[32px] border border-neutral-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-neutral-50 bg-neutral-50/30 flex items-center gap-2">
            <div className="w-1.5 h-4 bg-emerald-500 rounded-full" />
            <span className="text-[10px] font-bold uppercase text-neutral-500 tracking-[0.2em]">
              Assinatura Digital
            </span>
          </div>
          <div className="p-8 space-y-6">
            <div className="relative group">
              <div className="absolute inset-0 bg-neutral-900/[0.02] rounded-3xl -m-2 border-2 border-dashed border-neutral-200 group-hover:border-emerald-500/20 transition-all pointer-events-none" />
              <div className="relative border border-neutral-100 rounded-2xl bg-white overflow-hidden touch-none h-64 md:h-80 shadow-inner">
                <canvas 
                  ref={canvasRef}
                  width={1200}
                  height={400}
                  className="w-full h-full cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <div className="absolute bottom-4 right-4 flex gap-2 pointer-events-none transition-opacity">
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">Assine acima</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <Button 
                variant="ghost" 
                onClick={clearCanvas}
                className="h-12 px-8 rounded-2xl text-neutral-400 font-bold hover:bg-neutral-100 hover:text-neutral-600 transition-all flex items-center gap-2"
              >
                <Eraser className="w-4 h-4" />
                Limpar
              </Button>
              <Button 
                className="h-14 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-3 text-lg" 
                onClick={handleSign}
                disabled={signing}
              >
                {signing ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                Finalizar e Assinar
              </Button>
            </div>
          </div>
        </div>

        {/* Legal Footer */}
        <p className="text-center text-[10px] text-neutral-400 font-medium uppercase tracking-widest">
          Esta assinatura tem validade jurídica e é protegida por criptografia de ponta a ponta.
        </p>

      </div>
    </div>
  );
}
